import React, { FC, useState, useContext, useMemo, useEffect } from 'react';
import { useParams, useHistory, useLocation, Prompt } from 'react-router-dom';

import { Button, CircularProgress, Grid, Typography, Box } from '@mui/material';

import AcceptanceConfirmationModal from '../../../sharedModules/layout/AcceptanceConfirmationModal';
import ErrorMessage from '../../../sharedModules/layout/ErrorMessage';

import { backflowLogRequiredFields } from '../../backflowJobLists/constants/requiredFields';
import validateRequiredFields from '../../../utils/validateRequiredFields';

import { BackflowLog, BackflowJobItem } from '../../model';

import { SnackBarContext } from '../../../sharedModules/form/contexts/SnackBarContext';

import axios from '../../../sharedModules/utils/axios.utils';

interface BackflowLogSubmitButtonsProps {
  backflowJobItem: BackflowJobItem;
  formValues: BackflowLog;
  outOfService: boolean;
  fetchBackflowJobItem: () => void;
  requiredDeviceImageAttached?: boolean;
  requiredDataplateAttached?: boolean;
  onRequiredImagesValidationFailed?: () => void;
}

const BackflowLogSubmitButtons: FC<BackflowLogSubmitButtonsProps> = (props) => {
  const { formAction } = useParams<{ formAction?: string }>();
  const location = useLocation();

  const approveMode = formAction === 'approve';

  const history = useHistory();

  const {
    backflowJobItem,
    formValues,
    outOfService,
    fetchBackflowJobItem,
    requiredDeviceImageAttached = true,
    requiredDataplateAttached = true,
    onRequiredImagesValidationFailed,
  } = props;

  // Get backLink from URL params
  const searchParams = new URLSearchParams(location.search);
  const backLinkParam = searchParams.get('backLink');

  const { setSnackBarOpen, setSnackBarMessage, handleSnackbarAlertSeverityChange } = useContext(SnackBarContext);

  const [acceptanceConfirmationModalOpen, setAcceptanceConfirmationModalOpen] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState<boolean>(false);

  // If using the full form, many fields are required.
  // If using the comment-only form, only the comment is required.
  const requiredFields = outOfService
    ? [{ key: 'internal_comment', label: 'Comment' }]
    : backflowLogRequiredFields(backflowJobItem.backflow_type);

  const submitStatus = () => {
    if (outOfService) return 'out_of_service';
    if (approveMode) return 'approved';
    return 'pending_approval';
  };

  const submitJobItem = async () => {
    if (loading) return; // guard double-taps, including modal confirm

    try {
      setLoading(true);

      await axios.put(`/backflows/backflow_logs/${formValues.id}`, {
        ...formValues,
        status: submitStatus(),
      });

      // Refetch the job item to get the updated submitted_at value
      await fetchBackflowJobItem();

      setSnackBarMessage('Backflow test form submitted successfully!');
      handleSnackbarAlertSeverityChange('success');
      setSnackBarOpen(true);
      setErrorMessage('');

      // Redirect after a short delay
      let redirectUrl = '';

      // If there's a backLink parameter, use that
      if (backLinkParam) {
        redirectUrl = backLinkParam;
      } else {
        // Otherwise, default to job group page
        let urlSuffix = '';
        if (approveMode) urlSuffix = '/approve';
        redirectUrl = `/backflow_tests/backflow_job_groups/${backflowJobItem?.backflow_job_group_id}${urlSuffix}`;
      }

      setTimeout(() => {
        history.push(redirectUrl);
      }, 2000);
    } catch (error) {
      setErrorMessage('Failed to submit form. Please try again.');
      setLoading(false);
    }
  };

  const handleSubmitClicked = async () => {
    setHasAttemptedSubmit(true);

    if (backflowJobItem?.missing_fields?.length > 0) return;

    const validate = validateRequiredFields(formValues, requiredFields);
    if (!validate.isValid) return;

    if (!outOfService && (!requiredDeviceImageAttached || !requiredDataplateAttached)) {
      onRequiredImagesValidationFailed?.();
      return;
    }

    if (approveMode && backflowJobItem.is_final_approval_item) {
      setAcceptanceConfirmationModalOpen(true);
    } else {
      submitJobItem();
    }
  };

  const confirmMessage = (
    <p>
      This will approve the final Backflow Device for this work order. Doing so will notify all Tudi employees who
      receive approval notifications and submit the updated device details to Servman. This cannot be undone.
    </p>
  );

  const buttonLabel = () => {
    if (backflowJobItem?.status === 'approved') return 'Approved';
    if (approveMode) return 'Approve';
    return 'Submit for Approval';
  };

  const buttonDisabled = loading || backflowJobItem?.status === 'approved';

  // Compute incomplete fields in real-time for the checklist
  const incompleteItems = useMemo(() => {
    if (backflowJobItem?.status === 'approved') return [];

    const items: string[] = [];

    // Missing device details from backend
    if (backflowJobItem?.missing_fields?.length > 0) {
      backflowJobItem.missing_fields.forEach((field) => items.push(field));
    }

    // Missing form fields
    const validation = validateRequiredFields(formValues, requiredFields);
    validation.missingLabels.forEach((label) => items.push(label));

    // Missing required images
    if (!outOfService) {
      if (!requiredDeviceImageAttached) {
        items.push('Backflow Device Image');
      }
      if (!requiredDataplateAttached) {
        items.push('Capture Backflow Dataplate');
      }
    }

    return items;
  }, [
    formValues,
    requiredFields,
    backflowJobItem,
    outOfService,
    requiredDeviceImageAttached,
    requiredDataplateAttached,
  ]);

  const isFormIncomplete = incompleteItems.length > 0 && backflowJobItem?.status !== 'approved';

  useEffect(() => {
    if (isFormIncomplete) {
      window.onbeforeunload = () => 'This form is not complete. Are you sure you want to leave?';
    }
    return () => {
      window.onbeforeunload = null;
    };
  }, [isFormIncomplete]);

  return (
    <Grid container>
      <Prompt when={isFormIncomplete} message="This form is not complete. Are you sure you want to leave?" />
      <AcceptanceConfirmationModal
        onConfirm={submitJobItem}
        message={confirmMessage}
        modalOpen={acceptanceConfirmationModalOpen}
        setModalOpen={setAcceptanceConfirmationModalOpen}
        title="Approve Final Device for this Work Order"
      />
      <Grid item xs={12}>
        <ErrorMessage message={errorMessage} />
      </Grid>
      <Grid item xs={12}>
        <Button
          variant="contained"
          color="primary"
          size="large"
          onClick={handleSubmitClicked}
          disabled={buttonDisabled}
          sx={{ minWidth: 150 }}
        >
          {loading ? <CircularProgress size={24} /> : buttonLabel()}
        </Button>
      </Grid>
      {hasAttemptedSubmit && isFormIncomplete && (
        <Grid item xs={12}>
          <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1, border: 1, borderColor: 'grey.300' }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Required fields not yet completed:
            </Typography>
            <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
              {incompleteItems.map((item) => (
                <Typography component="li" variant="body2" color="text.secondary" key={item} sx={{ py: 0.25 }}>
                  {item}
                </Typography>
              ))}
            </Box>
          </Box>
        </Grid>
      )}
    </Grid>
  );
};

export default BackflowLogSubmitButtons;
